// Named-settings -> fluid-bg share-hash encoder.
//
// The published `fluid-bg` package's public API (fluidBackground / mountNative)
// only accepts a share-hash string via `opts.hash` — the field/layer/color
// engine config lives inside its bundled renderer and isn't exposed as a raw
// object. This keeps the *real* settings here as plain, readable values and
// encodes them into the hash format the package needs. Index positions match
// the hash the fluid-core engine's own shareUrl()/hash-parser use — see
// node_modules/fluid-bg/dist/core.js (functions `w` and `v.shareUrl`).

const FIELDS = [
  'noise', 'flow', 'cellular', 'gyroid', 'truchet', 'interfere', 'kaleido',
  'lines', 'grid', 'golden', 'smoke', 'crystal', 'honeycomb', 'bloom',
  'sweep', 'marble', 'plaid', 'curtain', 'stitch', 'pursuit', 'chladni',
  'cassini', 'topo', 'eddy',
];

const BLENDS = ['normal', 'multiply', 'screen', 'add', 'difference', 'overlay'];

const SCREENS = ['square', 'hex', 'ascii', 'dither', 'glitch'];

const MATERIALS = ['none', 'glass', 'metal', 'sand', 'liquid', 'molten', 'paint'];

const LENSES = [
  'none', 'square', 'invert', 'mobius', 'droste', 'hyperbolic', 'julia',
  'cube', 'exp', 'sine', 'joukowski', 'newton', 'modular', 'ground',
];

function indexOf(list, name, label) {
  const i = list.indexOf(name);
  if (i < 0) {
    throw new Error(`fluidBgHash: unknown ${label} "${name}" — valid: ${list.join(', ')}`);
  }
  return i;
}

// Same packing fluid-core's own hash encoder uses: a hex color straight to
// its 0xRRGGBB decimal value.
function packColor(hex) {
  return parseInt(hex.replace('#', ''), 16);
}

/**
 * Build a `#p=...` fluid-bg share hash from named settings.
 *
 * @param {object} cfg
 * @param {number} cfg.speed
 * @param {number} cfg.zoom
 * @param {number} cfg.warp
 * @param {number} cfg.grain
 * @param {number} cfg.pixel - pixelation amount
 * @param {number} cfg.dot - dot size
 * @param {boolean} [cfg.dots] - dot-screen mode on/off
 * @param {string} cfg.screen - one of SCREENS
 * @param {number} [cfg.sym] - symmetry, default 0
 * @param {number} cfg.seed
 * @param {string[]} cfg.colors - 4 hex stops, dark -> light
 * @param {number} [cfg.thresh] - 0-1, default 0.5
 * @param {string} cfg.field - base field (layer 1), one of FIELDS
 * @param {{field: string, blend: string, mix: number}} cfg.layer - layer 2 (mix 0-100)
 * @param {{field: string, blend: string, mix: number}} cfg.layer2 - layer 3 (mix 0-100)
 * @param {string} [cfg.material] - default 'none'
 * @param {string} [cfg.lens] - default 'none'
 * @param {number} [cfg.lensAmt] - 0-100, default 100
 */
export function buildFluidHash({
  speed, zoom, warp, grain,
  pixel, dot, dots = false,
  screen, sym = 0,
  seed,
  colors,
  thresh = 0.5,
  field,
  layer,
  layer2,
  material = 'none',
  lens = 'none',
  lensAmt = 100,
}) {
  const p = [
    speed, zoom, warp, grain,
    pixel, dot, dots ? 1 : 0,
    8, // palette: 8 = custom 4-stop colors
    seed,
    0.8, 0.85, // reserved — fixed by the engine, not user-adjustable
    1, // aspect ratio — metadata only; actual canvas size is used at render
    0, 0, // reserved
    indexOf(FIELDS, field, 'field'),
    // 'none' -> 0, same as the engine's own screen handling
    screen === 'none' ? 0 : indexOf(SCREENS, screen, 'screen'),
    0, 0, // reserved
    sym,
    0, // reserved
    ...colors.map(packColor),
    +(thresh - 0.5).toFixed(2),
    indexOf(FIELDS, layer.field, 'layer.field'),
    indexOf(BLENDS, layer.blend, 'layer.blend'),
    layer.mix,
    indexOf(MATERIALS, material, 'material'),
    indexOf(LENSES, lens, 'lens'),
    lensAmt,
    indexOf(FIELDS, layer2.field, 'layer2.field'),
    indexOf(BLENDS, layer2.blend, 'layer2.blend'),
    layer2.mix,
  ];

  return `#p=${p.join(',')}`;
}
